import React, { ComponentProps, PropsWithChildren } from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  DisplayPosition,
  Space,
  useTheme,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { Label } from './Label'
import { DietaryWarningTag } from '../DietaryWarningTag/DietaryWarningTag'
import { Recipe } from '@library/menu-service'

type LabelWrapperProps = {
  isOutOfStock: boolean
  recipe: Recipe
  reserveCtaSpace?: boolean
}

type LabelWrapperBoxProps = PropsWithChildren<{
  reserveCtaSpace?: boolean
}>

const BaseLabelWrapperBox = ({
  reserveCtaSpace: _reserveCtaSpace,
  ...props
}: LabelWrapperBoxProps & ComponentProps<typeof Box>) => <Box {...props} />

const LabelWrapperBox = withCitrusTheme(
  BaseLabelWrapperBox,
  (theme, { reserveCtaSpace }: LabelWrapperBoxProps) => ({
    display: Display.Flex,
    borderColor: theme.tokens.colors.White,
    borderWidth: 1.5,
    alignItems: AlignItems.Center,
    position: DisplayPosition.Absolute,
    top: 0,
    left: 0,
    transform: 'translateY(calc(-50% - 1rem))',
    zIndex: 12,
    minWidth: 0,
    maxWidth: reserveCtaSpace ? 'calc(100% - 56px)' : '100%',
    overflow: 'hidden',
  }),
)

export const LabelWrapper = ({
  isOutOfStock,
  recipe,
  reserveCtaSpace = false,
}: LabelWrapperProps) => {
  const { theme } = useTheme()

  const { invalidPreference } = recipe
  const brandTag = recipe.merchandisingTags.tagline
  const recipeTag = recipe.merchandisingTags.availability

  if (isOutOfStock) {
    return (
      <LabelWrapperBox data-testid="sold-out-tag">
        <Label
          text="Sold Out"
          textColor={Color.White}
          backgroundColor={theme.tokens.colors.ColdGrey_800}
        />
      </LabelWrapperBox>
    )
  }

  if (invalidPreference) {
    return (
      <LabelWrapperBox>
        <DietaryWarningTag text={invalidPreference} />
      </LabelWrapperBox>
    )
  }

  //if no tags, return
  if (!brandTag && !recipeTag) {
    return null
  }

  return (
    <LabelWrapperBox reserveCtaSpace={reserveCtaSpace}>
      {recipeTag && (
        <>
          <Label
            text={recipeTag.name}
            backgroundColor={recipeTag.backgroundColor.light}
            textColor={recipeTag.color.light}
          />
          <Space size={1} direction="horizontal" />
        </>
      )}

      {brandTag && (
        <Label
          text={brandTag.name}
          backgroundColor={brandTag.backgroundColor.light}
          textColor={brandTag.color.light}
        />
      )}
    </LabelWrapperBox>
  )
}
